import type { Evaluation } from "@/types/evaluation";
import { evalCompletion } from "@/utils/evalCompletion";

function formatDate(ts: number) {
  const d = new Date(ts);
  return {
    date: d.toLocaleDateString(),
    time: d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
  };
}

function EvalCardDate({ ts }: Readonly<{ ts: number }>) {
  const { date, time } = formatDate(ts);
  return (
    <div className="eval-card-date">
      <span>{date}</span>
      <span>{time}</span>
    </div>
  );
}

function DeleteButton({
  ev,
  onDelete,
}: Readonly<{ ev: Evaluation; onDelete: (target: { id: string; name: string }) => void }>) {
  return (
    <button
      className="danger"
      onClick={(e) => {
        e.stopPropagation();
        onDelete({ id: ev.id, name: ev.name });
      }}
    >
      Delete
    </button>
  );
}

export function InProgressCard({
  ev,
  onDelete,
  onNavigate,
}: Readonly<{
  ev: Evaluation;
  onNavigate: (id: string) => void;
  onDelete: (target: { id: string; name: string }) => void;
}>) {
  const completion = evalCompletion(ev);
  return (
    <div
      className="eval-card"
      onClick={() => {
        onNavigate(ev.id);
      }}
    >
      <EvalCardDate ts={ev.updatedAt} />
      <div className="eval-card-body">
        <div className="eval-card-header">
          <strong>{ev.name}</strong>
          <DeleteButton ev={ev} onDelete={onDelete} />
        </div>
        <div className="eval-card-meta">
          <span className="eval-card-template">{ev.templateName}</span>
        </div>
        <div className="eval-card-progress">
          <div className="eval-progress-bar">
            <div
              className="eval-progress-fill"
              style={{
                width:
                  completion.total > 0
                    ? `${(completion.completed / completion.total) * 100}%`
                    : "0%",
              }}
            />
          </div>
          <span className="eval-progress-text">
            {completion.completed}/{completion.total} fields
          </span>
        </div>
      </div>
    </div>
  );
}

export function CompletedCard({
  ev,
  onDelete,
  onNavigate,
}: Readonly<{
  ev: Evaluation;
  onNavigate: (id: string) => void;
  onDelete: (target: { id: string; name: string }) => void;
}>) {
  const completion = evalCompletion(ev);
  return (
    <div
      className="eval-card eval-card-completed"
      onClick={() => {
        onNavigate(ev.id);
      }}
    >
      <EvalCardDate ts={ev.completedAt ?? ev.updatedAt} />
      <div className="eval-card-body">
        <div className="eval-card-header">
          <strong>{ev.name}</strong>
          <span className="eval-completed-badge">Completed</span>
          <DeleteButton ev={ev} onDelete={onDelete} />
        </div>
        <div className="eval-card-meta">
          <span className="eval-card-template">{ev.templateName}</span>
        </div>
        <div className="eval-card-progress">
          <span className="eval-progress-text">
            {completion.completed}/{completion.total} fields
          </span>
        </div>
      </div>
    </div>
  );
}
